// Import React library - needed for JSX and React functionality
import React, { useState, useEffect } from 'react';
import { AUTH_BASE } from './config';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
// Import our components
import Home from './components/Home';
import Login from './components/Login';
import Dashboard from './components/Dashboard';
import PurchaseSuccess from './components/PurchaseSuccess';
import LoadingSpinner from './components/LoadingSpinner';
import AdminDashboard from './components/admin/AdminDashboard';
import HelpPage from './components/HelpPage';
// Import styles for this component
import './App.css';
import { ChatProvider } from './context/ChatContext';
import { ToastProvider } from './context/ToastContext';

/**
 * App Component - The root component of our application
 * Handles authentication state and renders either Login or Dashboard
 */
function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Function to check auth status
  const checkAuthStatus = async () => {
    try {
      const response = await fetch(`${AUTH_BASE}/status`, {
        credentials: 'include',
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        }
      });
      const data = await response.json();
      
      if (data.isAuthenticated && data.user) {
        setIsAuthenticated(true);
        setUser(data.user);
        // If we're on the login page or root, redirect to dashboard
        const currentPath = window.location.pathname;
        if (currentPath === '/login' || currentPath === '/') {
          window.location.href = `/dashboard/${data.user._id}`;
        }
      } else {
        setIsAuthenticated(false);
        setUser(null);
      }
    } catch (err) {
      console.error('Error checking auth status:', err);
      setIsAuthenticated(false);
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  };

  // Preserve deep-link params (?listing=, ?view=) across the login/redirect round-trip.
  // Routing strips the query when sending /dashboard → /dashboard/:userId or → /login,
  // so we stash the relevant params here (at mount, before any redirect) and let the
  // Dashboard consume them once it loads. Captured for both logged-out and logged-in users.
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.has('listing') || params.has('view')) {
        const keep = new URLSearchParams();
        if (params.get('listing')) keep.set('listing', params.get('listing'));
        if (params.get('view')) keep.set('view', params.get('view'));
        localStorage.setItem(
          'utrade_pendingDeepLink',
          JSON.stringify({ q: keep.toString(), t: Date.now() })
        );
      }
    } catch (e) { /* ignore storage errors */ }
  }, []);

  // Check auth status on mount and when URL changes
  useEffect(() => {
    checkAuthStatus();
    // Add event listener for URL changes
    window.addEventListener('popstate', checkAuthStatus);
    return () => window.removeEventListener('popstate', checkAuthStatus);
  }, []);

  // Add an effect to monitor authentication state changes
  useEffect(() => {
    if (!isAuthenticated) {
      setUser(null);
    }
  }, [isAuthenticated]);

  if (isLoading) {
    return (
      <div style={{
        display:'flex',
        justifyContent:'center',
        alignItems:'center',
        height:'100vh',
        background:'#f8f9fa'
      }}>
        <LoadingSpinner size={60}/>
      </div>
    );
  }

  return (
    <ToastProvider>
    <ChatProvider currentUser={user}>
    <Router>
      <div className="App">
        <Routes>
          {/* Home route */}
          <Route 
            path="/" 
            element={
              isAuthenticated && user ? 
                <Navigate to={`/dashboard/${user._id}`} replace /> : 
                <Home />
            } 
          />

          {/* Login route - redirect to user's dashboard if already authenticated */}
          <Route 
            path="/login" 
            element={
              isAuthenticated && user ? 
                <Navigate to={`/dashboard/${user._id}`} replace /> : 
                <Login />
            } 
          />

          {/* Protected dashboard route with user ID */}
          <Route 
            path="/dashboard/:userId" 
            element={
              isAuthenticated && user ? (
                user._id === window.location.pathname.split('/')[2] ? (
                  <Dashboard setIsAuthenticated={setIsAuthenticated} setUser={setUser} user={user} />
                ) : (
                  <Navigate to={`/dashboard/${user._id}`} replace />
                )
              ) : (
                <Navigate to="/login" replace />
              )
            } 
          />

          {/* Redirect /dashboard to user-specific dashboard if authenticated */}
          <Route 
            path="/dashboard" 
            element={
              isAuthenticated && user ? 
                <Navigate to={`/dashboard/${user._id}`} replace /> : 
                <Navigate to="/login" replace />
            } 
          />

          {/* Purchase success return URL from Stripe */}
          <Route path="/purchase/success" element={<PurchaseSuccess />} />

          <Route path="/help" element={<HelpPage />} />

          {/* Admin dashboard — server-side isAdmin middleware is the real gate */}
          <Route
            path="/admin"
            element={
              !isAuthenticated ? <Navigate to="/login" replace />
              : (user?.isAdmin || ['hchhabra@umass.edu'].includes((user?.email || '').toLowerCase()))
                ? <AdminDashboard user={user} />
                : <Navigate to="/dashboard" replace />
            }
          />

          {/* Catch all route - redirect to home */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </div>
    </Router>
    </ChatProvider>
    </ToastProvider>
  );
}

// Export the App component so it can be imported and used in index.js
export default App;
